// Phase 43.59 — Customer Home "Ordini attivi" live block
// Mostra l'ordine più avanzato/prioritario in alto Home, con stato umano,
// ETA, CTA "Apri tracking". Badge "+N altri ordini" se ce ne sono altri.
//
// Linguaggio umano (no stati tecnici): "Cerchiamo un driver", "Driver
// assegnato", "In viaggio verso il ritiro", "Pacco ritirato · in consegna",
// "Consegnato".
import { Link } from "react-router-dom";
import { Package, MapPin, Clock, ArrowRight, Search, Truck, CheckCircle2 } from "lucide-react";

// Phase 44.12 — Fix critico "Da pagare" mostrato anche su ordini PAGATI.
//
// Root cause: lo `status` lifecycle ordine resta "pending" anche dopo che
// `payment_status` è stato spostato a "paid" (es. coperto da subscription,
// delivery_pack, o webhook Stripe completato) finché un driver non accetta.
// Mostravamo "Da pagare" per qualunque `status=pending`, rompendo la fiducia
// (l'utente vedeva "Da pagare" su un ordine appena pagato).
//
// Fix: deriviamo uno `effectiveStatus` payment-aware. Se l'ordine è già
// "paid" sul lato pagamento, lo trattiamo come "matching" (Cerchiamo un
// driver), che è semanticamente corretto.
function deriveEffectiveStatus(order) {
  if (!order) return "pending";
  const s = order.status;
  if (s !== "pending") return s;
  // pending + paid → in realtà siamo in fase matching driver
  return order.payment_status === "paid" ? "matching" : "pending";
}
const STATUS_RANK = {
  pending: 1,
  paid: 2,
  matching: 3,
  accepted: 4,
  picking_up: 5,
  picked_up: 6,
  delivering: 7,
};

const STATUS_BLOCK = {
  // Phase 43.61 — Cromia unificata Driver/Customer:
  //   ARANCIONE #F59E0B = prima del ritiro (paid/matching/accepted/picking_up)
  //   BLU      #0052FF  = pacco ritirato/in consegna
  //   VERDE    #16A34A  = consegnato
  pending: { label: "Da pagare", Icon: Clock, dot: "#F59E0B" },
  paid: { label: "Cerchiamo un driver", Icon: Search, dot: "#F59E0B" },
  matching: { label: "Cerchiamo un driver", Icon: Search, dot: "#F59E0B" },
  accepted: { label: "Driver assegnato · in viaggio al ritiro", Icon: Truck, dot: "#F59E0B" },
  picking_up: { label: "Driver al punto di ritiro", Icon: MapPin, dot: "#F59E0B" },
  picked_up: { label: "Pacco ritirato · in consegna", Icon: Truck, dot: "#0052FF" },
  delivering: { label: "In consegna al destinatario", Icon: Truck, dot: "#0052FF" },
  delivered: { label: "Consegnato", Icon: CheckCircle2, dot: "#16A34A" },
};

function formatEta(o) {
  // ETA stimata se presente nei payload server (eta_min_to_pickup/delivery) — best-effort
  const phase = o.status;
  const m = phase === "picked_up" || phase === "delivering"
    ? (o.eta_min_to_delivery || o.eta_to_destination_min)
    : (o.eta_min_to_pickup || o.eta_to_destination_min);
  if (!m || m <= 0) return null;
  if (m < 1) return "Arrivo imminente";
  if (m < 60) return `Arrivo stimato ${Math.round(m)} min`;
  return `Arrivo stimato ${Math.floor(m / 60)}h ${Math.round(m % 60)}min`;
}

export default function CustomerActiveOrdersBlock({ orders }) {
  const active = (orders || [])
    .filter((o) => !["delivered", "cancelled"].includes(o.status))
    .sort((a, b) => (STATUS_RANK[deriveEffectiveStatus(b)] || 0) - (STATUS_RANK[deriveEffectiveStatus(a)] || 0));

  if (active.length === 0) return null;
  const primary = active[0];
  const extras = active.length - 1;
  const effectiveStatus = deriveEffectiveStatus(primary);
  const cfg = STATUS_BLOCK[effectiveStatus] || STATUS_BLOCK.paid;
  const Icon = cfg.Icon;
  const eta = formatEta(primary);
  const shortId = (primary.id || "").slice(0, 8).toUpperCase();
  const coveredByPack = primary.payment_source === "delivery_pack";

  return (
    <section
      data-testid="customer-active-orders-block"
      className="rounded-3xl border border-dapnow-line bg-white shadow-md overflow-hidden"
    >
      <header className="flex items-center justify-between px-4 sm:px-5 pt-4 pb-2.5">
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-dapnow-blue" />
          <p className="overline text-dapnow-blue">
            {active.length === 1 ? "Ordine attivo" : `Ordini attivi · ${active.length}`}
          </p>
        </div>
        {extras > 0 && (
          <Link
            to="/app/orders"
            className="text-[11px] font-semibold text-dapnow-muted hover:text-dapnow-ink transition"
            data-testid="customer-active-orders-extras"
          >
            +{extras} altr{extras === 1 ? "o" : "i"} →
          </Link>
        )}
      </header>

      <div className="px-4 sm:px-5 pb-4 space-y-3">
        <div className="flex items-center gap-2">
          <span className="relative inline-flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full opacity-70 animate-ping" style={{ background: cfg.dot }} />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: cfg.dot }} />
          </span>
          <p className="font-mono text-xs text-dapnow-muted" data-testid="customer-active-order-id">#{shortId}</p>
        </div>

        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 shrink-0" style={{ color: cfg.dot }} />
          <p className="font-display text-base sm:text-lg font-bold text-dapnow-ink tracking-tight leading-tight" data-testid="customer-active-order-state">
            {cfg.label}
          </p>
        </div>

        {eta && (
          <p className="text-xs text-dapnow-muted flex items-center gap-1.5" data-testid="customer-active-order-eta">
            <Clock className="h-3.5 w-3.5" />
            {eta}
          </p>
        )}

        {coveredByPack && (
          <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] font-semibold text-emerald-900" data-testid="covered-by-pack-pill">
            ✦ Coperto dal tuo Credito Consegne
          </div>
        )}

        <Link
          to={`/app/orders/${primary.id}`}
          data-testid="customer-active-order-open"
          className="group mt-1 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-dapnow-ink text-white px-4 h-11 text-sm font-bold tracking-tight hover:bg-dapnow-blue transition"
        >
          Apri tracking
          <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
        </Link>
      </div>
    </section>
  );
}
